import React, { useState, useRef, useCallback, lazy, Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '@google/model-viewer';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { 
  Wand2, Loader2, Download, ZoomIn, Sparkles,
  Image as ImageIcon, Palette, Grid3X3, LayoutGrid,
  Building2, Brush, Upload, X, ImagePlus, Paintbrush,
  Home, Castle, TreePine, Lightbulb, Layers, Mountain,
  ArrowRight, RefreshCw, ShoppingBag, Megaphone,
  Sofa, Sun, Moon, Camera, Box, Compass, PenTool,
  Layout, Columns, Grid, Monitor, Smartphone, FileImage,
  Video, Play, Scan, Package, Coins, FileText, Gem, Watch, Shirt, RotateCcw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import api from '../../utils/api';

// Lazy load 3D viewer and Plan Render tab
const ModelViewer3D = lazy(() => import('../3d/ModelViewer3D'));
const PlanRenderTab = lazy(() => import('../forge/PlanRenderTab'));

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// EXTERIOR ARCHITECTURE STYLES
// ============================================
const EXTERIOR_STYLES = [
  { id: 'modern', name: 'Modern', icon: Building2, prompt: 'modern architecture, minimalist, clean lines, glass and steel, contemporary design' },
  { id: 'parametric', name: 'Parametric', icon: Compass, prompt: 'parametric architecture, Zaha Hadid style, flowing curves, futuristic organic forms' },
  { id: 'brutalist', name: 'Brutalist', icon: Layers, prompt: 'brutalist architecture, raw concrete, bold geometric forms, monumental' },
  { id: 'classical', name: 'Classical', icon: Castle, prompt: 'classical architecture, columns, symmetry, marble, neoclassical elegance' },
  { id: 'sustainable', name: 'Sustainable', icon: TreePine, prompt: 'sustainable green architecture, living walls, solar panels, eco-friendly design' },
  { id: 'japanese', name: 'Japanese', icon: Home, prompt: 'Japanese architecture, zen minimalism, wood, harmony with nature, tatami' },
  { id: 'art-deco', name: 'Art Deco', icon: Sparkles, prompt: 'art deco architecture, geometric patterns, luxury, golden details, 1920s glamour' },
  { id: 'industrial', name: 'Industrial', icon: Box, prompt: 'industrial architecture, exposed brick, metal beams, warehouse conversion, loft style' },
];

// ============================================
// INTERIOR DESIGN STYLES
// ============================================
const INTERIOR_STYLES = [
  { id: 'modern-minimal', name: 'Minimalist', icon: '◻️', prompt: 'minimalist interior design, clean lines, neutral colors, uncluttered, zen aesthetic' },
  { id: 'scandinavian', name: 'Scandinavian', icon: '🪵', prompt: 'scandinavian interior design, hygge, light wood, white walls, cozy textiles' },
  { id: 'industrial', name: 'Industrial', icon: '🏭', prompt: 'industrial interior design, exposed brick, metal pipes, raw materials, loft' },
  { id: 'mid-century', name: 'Mid-Century', icon: '🛋️', prompt: 'mid-century modern interior, retro furniture, organic shapes, warm wood tones' },
  { id: 'luxury', name: 'Luxury', icon: '✨', prompt: 'luxury interior design, marble, gold accents, crystal chandeliers, opulent' },
  { id: 'bohemian', name: 'Bohemian', icon: '🌿', prompt: 'bohemian interior design, eclectic, plants, colorful textiles, layered textures' },
  { id: 'japandi', name: 'Japandi', icon: '🎋', prompt: 'japandi interior design, japanese minimalism meets scandinavian, wabi-sabi' },
  { id: 'contemporary', name: 'Contemporary', icon: '🖼️', prompt: 'contemporary interior design, current trends, statement pieces, bold art' },
];

// Room Types for Interior
const ROOM_TYPES = [
  { id: 'living', name: 'Living Room', prompt: 'living room interior, comfortable seating, natural light' },
  { id: 'bedroom', name: 'Bedroom', prompt: 'bedroom interior, comfortable bed, relaxing atmosphere' },
  { id: 'kitchen', name: 'Kitchen', prompt: 'kitchen interior, modern appliances, functional design' },
  { id: 'bathroom', name: 'Bathroom', prompt: 'bathroom interior, spa-like, clean tiles, elegant fixtures' },
  { id: 'office', name: 'Home Office', prompt: 'home office interior, productive workspace, ergonomic' },
  { id: 'dining', name: 'Dining Room', prompt: 'dining room interior, elegant table setting, ambient lighting' },
];

// ============================================
// 3D RENDER STYLES
// ============================================
const RENDER_STYLES = [
  { id: 'vray', name: 'V-Ray', icon: '🎯', prompt: 'V-Ray render, photorealistic, accurate lighting, ray tracing, architectural visualization' },
  { id: 'corona', name: 'Corona', icon: '☀️', prompt: 'Corona render, soft lighting, realistic materials, architectural render' },
  { id: 'lumion', name: 'Lumion', icon: '🌅', prompt: 'Lumion style render, atmospheric, cinematic lighting, landscape integration' },
  { id: 'enscape', name: 'Enscape', icon: '🏠', prompt: 'Enscape real-time render, clean, bright, architectural walkthrough style' },
  { id: 'octane', name: 'Octane', icon: '⚡', prompt: 'Octane render, GPU rendering, cinematic quality, volumetric lighting' },
  { id: 'unreal', name: 'Unreal', icon: '🎮', prompt: 'Unreal Engine render, real-time graphics, ray tracing, game-quality visualization' },
  { id: 'blender', name: 'Blender Cycles', icon: '🔵', prompt: 'Blender Cycles render, path tracing, open-source quality, realistic' },
  { id: 'sketch', name: 'Concept Sketch', icon: '✏️', prompt: 'architectural concept sketch, hand-drawn style, watercolor wash, design development' },
];

// Render View Types
const VIEW_TYPES = [
  { id: 'exterior', name: 'Exterior', prompt: 'exterior view, facade, street perspective' },
  { id: 'interior', name: 'Interior', prompt: 'interior perspective, eye-level view' },
  { id: 'aerial', name: 'Aerial/Drone', prompt: 'aerial view, drone perspective, birds eye' },
  { id: 'section', name: 'Section Cut', prompt: 'architectural section, cross-section, cutaway view' },
  { id: 'elevation', name: 'Elevation', prompt: 'building elevation, orthographic, facade drawing' },
  { id: 'axonometric', name: 'Axonometric', prompt: 'axonometric view, isometric, 3D diagram' },
];

// Lighting Conditions
const LIGHTING_CONDITIONS = [
  { id: 'daylight', name: 'Daylight', prompt: 'natural daylight, soft shadows, midday sun' },
  { id: 'golden', name: 'Golden Hour', prompt: 'golden hour lighting, warm sunset, long shadows' },
  { id: 'blue', name: 'Blue Hour', prompt: 'blue hour, twilight, city lights beginning' },
  { id: 'night', name: 'Night', prompt: 'night scene, artificial lighting, dramatic illumination' },
  { id: 'overcast', name: 'Overcast', prompt: 'overcast sky, soft diffused light, no harsh shadows' },
  { id: 'studio', name: 'Studio', prompt: 'studio lighting setup, controlled, professional' },
];

// ============================================
// LANDSCAPE ARCHITECTURE STYLES
// ============================================
const LANDSCAPE_STYLES = [
  { id: 'modern-garden', name: 'Modern', icon: '🏡', prompt: 'modern landscape design, geometric forms, clean lines, minimalist garden' },
  { id: 'japanese-garden', name: 'Japanese', icon: '⛩️', prompt: 'Japanese zen garden, koi pond, stone lanterns, raked gravel, bonsai' },
  { id: 'mediterranean', name: 'Mediterranean', icon: '🌴', prompt: 'mediterranean landscape, olive trees, terracotta, drought-tolerant plants' },
  { id: 'tropical', name: 'Tropical', icon: '🌺', prompt: 'tropical landscape design, palm trees, exotic flowers, lush greenery, pool' },
  { id: 'english', name: 'English Garden', icon: '🌹', prompt: 'English cottage garden, roses, perennials, romantic, wildflower meadow' },
  { id: 'urban', name: 'Urban Plaza', icon: '🏢', prompt: 'urban landscape design, public plaza, seating, water feature, hardscape' },
  { id: 'rooftop', name: 'Rooftop', icon: '🌿', prompt: 'rooftop garden, green roof, urban farming, sustainable, city views' },
  { id: 'resort', name: 'Resort', icon: '🏖️', prompt: 'resort landscape, infinity pool, cabanas, luxury outdoor living' },
];

// ============================================
// PRODUCT VISUALIZATION STYLES - EXPANDED
// ============================================

// Product Categories
const PRODUCT_CATEGORIES = [
  { id: 'jewelry', name: 'Jewelry', icon: '💎', description: 'Rings, necklaces, earrings, bracelets' },
  { id: 'watches', name: 'Watches', icon: '⌚', description: 'Luxury timepieces and smartwatches' },
  { id: 'fashion', name: 'Fashion', icon: '👗', description: 'Clothing, bags, shoes, accessories' },
  { id: 'electronics', name: 'Electronics', icon: '📱', description: 'Phones, laptops, gadgets' },
  { id: 'cosmetics', name: 'Cosmetics', icon: '💄', description: 'Beauty, skincare, perfume' },
  { id: 'furniture', name: 'Furniture', icon: '🛋️', description: 'Home and office furniture' },
  { id: 'lighting', name: 'Lighting', icon: '💡', description: 'Lamps, fixtures, decorative lights' },
  { id: 'homeware', name: 'Homeware', icon: '🏺', description: 'Decor, kitchenware, textiles' },
  { id: 'automotive', name: 'Automotive', icon: '🚗', description: 'Cars, parts, accessories' },
  { id: 'food', name: 'Food & Beverage', icon: '🍾', description: 'Packaging, bottles, culinary' },
];

// Product Visualization Styles
const PRODUCT_STYLES = [
  { id: 'minimal', name: 'Minimal Studio', icon: '⬜', prompt: 'minimalist product photography, clean white background, professional studio lighting, e-commerce style' },
  { id: 'luxury', name: 'Luxury', icon: '✨', prompt: 'luxury product shot, elegant, premium materials, gold accents, sophisticated, high-end catalog' },
  { id: 'lifestyle', name: 'Lifestyle', icon: '🏠', prompt: 'lifestyle product photography, in-context, natural setting, aspirational, editorial style' },
  { id: 'dramatic', name: 'Dramatic', icon: '🎭', prompt: 'dramatic product photography, strong contrast, cinematic lighting, bold shadows, artistic' },
  { id: 'floating', name: 'Floating', icon: '🎈', prompt: 'floating product shot, levitating, dynamic angle, motion blur effect, creative advertising' },
  { id: 'macro', name: 'Macro Detail', icon: '🔍', prompt: 'macro product photography, extreme close-up, texture detail, material quality visible' },
  { id: 'hero', name: 'Hero Shot', icon: '🦸', prompt: 'hero product shot, low angle, powerful presence, dominant in frame, advertising poster' },
  { id: 'flat-lay', name: 'Flat Lay', icon: '📐', prompt: 'flat lay product arrangement, top-down view, organized composition, social media style' },
  { id: 'environmental', name: 'Environmental', icon: '🌿', prompt: 'environmental product shot, natural elements, sustainable aesthetic, organic materials' },
  { id: 'tech', name: 'Tech Futuristic', icon: '💻', prompt: 'tech product render, sleek futuristic, holographic effects, gradient background, reflective' },
  { id: '3d-render', name: '3D CGI', icon: '🎮', prompt: 'high-end 3D CGI product render, ray-traced, photorealistic materials, studio setup' },
  { id: 'packaging', name: 'Packaging', icon: '📦', prompt: 'packaging design visualization, box mockup, label design, shelf display ready' },
];

// Metal/Material Types for Jewelry & Products
const PRODUCT_MATERIALS = [
  { id: 'none', name: 'Auto', icon: '🤖', prompt: '', category: 'default' },
  // Precious Metals
  { id: 'gold-24k', name: '24K Gold', icon: '🥇', prompt: 'pure 24 karat gold, bright yellow gold, highly polished, luxurious shine', category: 'metal' },
  { id: 'gold-18k', name: '18K Gold', icon: '✨', prompt: '18 karat gold, rich warm gold tone, premium finish', category: 'metal' },
  { id: 'rose-gold', name: 'Rose Gold', icon: '🌹', prompt: 'rose gold finish, pink gold, romantic copper-gold hue', category: 'metal' },
  { id: 'white-gold', name: 'White Gold', icon: '⚪', prompt: 'white gold, rhodium plated, silver-white precious metal', category: 'metal' },
  { id: 'platinum', name: 'Platinum', icon: '💎', prompt: 'platinum finish, silvery-white, dense luxury metal', category: 'metal' },
  { id: 'silver', name: 'Sterling Silver', icon: '🥈', prompt: 'sterling silver, polished, classic elegance', category: 'metal' },
  // Gemstones
  { id: 'diamond', name: 'Diamond', icon: '💠', prompt: 'brilliant cut diamond, sparkling facets, fire and brilliance, clarity', category: 'gem' },
  { id: 'ruby', name: 'Ruby', icon: '❤️', prompt: 'deep red ruby, pigeon blood color, faceted gemstone', category: 'gem' },
  { id: 'emerald', name: 'Emerald', icon: '💚', prompt: 'vivid green emerald, colombian emerald, included beauty', category: 'gem' },
  { id: 'sapphire', name: 'Sapphire', icon: '💙', prompt: 'royal blue sapphire, cornflower blue, faceted precious stone', category: 'gem' },
  { id: 'pearl', name: 'Pearl', icon: '🔮', prompt: 'lustrous pearl, nacre finish, iridescent sheen, elegant', category: 'gem' },
  // Finishes
  { id: 'polished', name: 'Polished', icon: '✨', prompt: 'highly polished mirror finish, reflective surface', category: 'finish' },
  { id: 'brushed', name: 'Brushed', icon: '〰️', prompt: 'brushed metal finish, satin texture, fine lines', category: 'finish' },
  { id: 'matte', name: 'Matte', icon: '⬛', prompt: 'matte finish, non-reflective, subtle elegance', category: 'finish' },
  { id: 'hammered', name: 'Hammered', icon: '🔨', prompt: 'hammered texture, artisanal finish, organic dimples', category: 'finish' },
  // Other Materials
  { id: 'leather', name: 'Leather', icon: '🟤', prompt: 'premium leather, genuine hide, grain texture visible', category: 'material' },
  { id: 'crystal', name: 'Crystal', icon: '🔷', prompt: 'crystal glass, light refraction, prismatic effect', category: 'material' },
  { id: 'ceramic', name: 'Ceramic', icon: '🏺', prompt: 'fine ceramic, porcelain finish, smooth surface', category: 'material' },
  { id: 'wood-luxury', name: 'Exotic Wood', icon: '🪵', prompt: 'exotic wood grain, ebony or rosewood, natural pattern', category: 'material' },
];

// Product Backgrounds - Expanded
const PRODUCT_BACKGROUNDS = [
  { id: 'white', name: 'Pure White', icon: '⬜', prompt: 'pure white seamless background, e-commerce standard', color: '#FFFFFF' },
  { id: 'soft-gray', name: 'Soft Gray', icon: '🔘', prompt: 'soft gray studio background, neutral tone', color: '#e5e5e5' },
  { id: 'black', name: 'Dark Black', icon: '⬛', prompt: 'deep black background, dramatic contrast', color: '#0a0a0a' },
  { id: 'cream', name: 'Warm Cream', icon: '🟡', prompt: 'warm cream background, cozy elegant', color: '#f5f0e6' },
  // Luxury Surfaces
  { id: 'marble-white', name: 'White Marble', icon: '🪨', prompt: 'white carrara marble surface, luxury setting, veined stone', color: '#f8f8f8' },
  { id: 'marble-black', name: 'Black Marble', icon: '🖤', prompt: 'black marble surface with gold veins, opulent', color: '#1a1a1a' },
  { id: 'velvet-red', name: 'Red Velvet', icon: '❤️', prompt: 'rich red velvet fabric background, jewelry display, luxury', color: '#8B0000' },
  { id: 'velvet-blue', name: 'Blue Velvet', icon: '💙', prompt: 'royal blue velvet backdrop, soft texture, elegant', color: '#000080' },
  { id: 'wood-dark', name: 'Dark Wood', icon: '🟫', prompt: 'dark walnut wood surface, grain visible, premium', color: '#3d2b1f' },
  { id: 'concrete', name: 'Concrete', icon: '🏗️', prompt: 'polished concrete surface, industrial chic, modern', color: '#808080' },
  // Gradients
  { id: 'gradient-gold', name: 'Gold Gradient', icon: '🌟', prompt: 'warm gold gradient background, luxury advertising', color: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)' },
  { id: 'gradient-silver', name: 'Silver Gradient', icon: '🌙', prompt: 'cool silver gradient background, modern premium', color: 'linear-gradient(135deg, #bdc3c7 0%, #2c3e50 100%)' },
  { id: 'gradient-rose', name: 'Rose Gradient', icon: '🌸', prompt: 'soft rose pink gradient, feminine luxury', color: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  // Natural/Lifestyle
  { id: 'nature', name: 'Natural Setting', icon: '🌿', prompt: 'natural organic background, leaves, plants, eco-friendly', color: '#2d5016' },
  { id: 'beach', name: 'Beach Sand', icon: '🏖️', prompt: 'sandy beach surface, shells, coastal lifestyle', color: '#c2b280' },
];

// Product Lighting Presets
const PRODUCT_LIGHTING = [
  { id: 'soft-box', name: 'Soft Box', icon: '☁️', prompt: 'soft diffused studio lighting, even illumination, no harsh shadows' },
  { id: 'rim-light', name: 'Rim Light', icon: '🌅', prompt: 'rim lighting, edge highlight, separation from background, dramatic' },
  { id: 'natural', name: 'Natural Light', icon: '☀️', prompt: 'natural daylight, window light, soft shadows, authentic' },
  { id: 'dramatic', name: 'Dramatic', icon: '🎭', prompt: 'dramatic spot lighting, strong shadows, high contrast, moody' },
  { id: 'golden-hour', name: 'Golden Hour', icon: '🌇', prompt: 'warm golden hour lighting, sunset tones, romantic atmosphere' },
  { id: 'neon', name: 'Neon Glow', icon: '💜', prompt: 'neon lighting, colorful glow effects, cyberpunk aesthetic' },
  { id: 'backlit', name: 'Backlit', icon: '🔆', prompt: 'backlit silhouette effect, halo glow, ethereal' },
  { id: 'spotlight', name: 'Spotlight', icon: '🔦', prompt: 'focused spotlight, vignette effect, center attention' },
];

// Product 3D Camera Presets
const PRODUCT_CAMERA_PRESETS = [
  { id: 'perspective', name: '3/4 View', orbit: '45deg 65deg 105%', icon: '📐' },
  { id: 'front', name: 'Front', orbit: '0deg 90deg 105%', icon: '⬆️' },
  { id: 'side', name: 'Side', orbit: '90deg 90deg 105%', icon: '➡️' },
  { id: 'top', name: 'Top', orbit: '0deg 0deg 120%', icon: '⬇️' },
  { id: 'hero', name: 'Hero', orbit: '30deg 70deg 90%', icon: '🦸' },
  { id: 'detail', name: 'Detail', orbit: '45deg 80deg 70%', icon: '🔍' },
];

// Product Ad Background Colors (keeping for compatibility)
const AD_BACKGROUNDS = [
  { id: 'white', name: 'Clean White', color: '#FFFFFF' },
  { id: 'black', name: 'Dark Black', color: '#0a0a0a' },
  { id: 'warm-gray', name: 'Warm Gray', color: '#f5f5f4' },
  { id: 'gradient-blue', name: 'Blue Gradient', color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'gradient-warm', name: 'Warm Gradient', color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'gradient-green', name: 'Green Gradient', color: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
];

const ASPECT_RATIOS = [
  { id: '1:1', name: 'Square (1:1)' },
  { id: '16:9', name: 'Landscape (16:9)' },
  { id: '9:16', name: 'Portrait (9:16)' },
  { id: '4:3', name: 'Standard (4:3)' },
  { id: '3:2', name: 'Photo (3:2)' },
  { id: '21:9', name: 'Cinematic (21:9)' },
];

const ImageGenerator = ({ images, onGenerate, onReTheme, isGenerating }) => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('exterior');
  const [creditBalance, setCreditBalance] = useState(null);
  
  // Fetch credit balance
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await api.get('/api/subscription/credits');
        setCreditBalance(response.data.balance);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      }
    };
    fetchCredits();
  }, [images.length]); // Refetch when images change
  
  // 3D Model renders gallery
  const [model3DRenders, setModel3DRenders] = useState([]);
  
  // 3D Model file persistence
  const [model3DFile, setModel3DFile] = useState(null);
  const [model3DUrl, setModel3DUrl] = useState(null);
  const [model3DForgeUrn, setModel3DForgeUrn] = useState(null);
  const [model3DForgeStatus, setModel3DForgeStatus] = useState(null);
  
  // Exterior states
  const [selectedExteriorStyle, setSelectedExteriorStyle] = useState('modern');
  
  // Interior states
  const [selectedInteriorStyle, setSelectedInteriorStyle] = useState('modern-minimal');
  const [selectedRoomType, setSelectedRoomType] = useState('living');
  
  // 3D Render states
  const [selectedRenderStyle, setSelectedRenderStyle] = useState('vray');
  const [selectedViewType, setSelectedViewType] = useState('exterior');
  const [selectedLighting, setSelectedLighting] = useState('daylight');
  
  // Landscape states
  const [selectedLandscapeStyle, setSelectedLandscapeStyle] = useState('modern-garden');
  
  // Product states - EXPANDED
  const [selectedProductStyle, setSelectedProductStyle] = useState('minimal');
  const [selectedAdBackground, setSelectedAdBackground] = useState('white');
  const [selectedProductCategory, setSelectedProductCategory] = useState('jewelry');
  const [selectedProductMaterial, setSelectedProductMaterial] = useState('none');
  const [selectedProductBackground, setSelectedProductBackground] = useState('white');
  const [selectedProductLighting, setSelectedProductLighting] = useState('soft-box');
  const [showProductMaterialPanel, setShowProductMaterialPanel] = useState(false);
  const [showProductBackgroundPanel, setShowProductBackgroundPanel] = useState(false);
  const [showProductLightingPanel, setShowProductLightingPanel] = useState(false);
  
  // Product 3D file upload
  const [product3DFile, setProduct3DFile] = useState(null);
  const [product3DUrl, setProduct3DUrl] = useState(null);
  const [product3DMode, setProduct3DMode] = useState(false); // Toggle between image upload and 3D upload
  const [productGlbUrl, setProductGlbUrl] = useState(null); // Converted GLB for model-viewer
  const [isConverting3D, setIsConverting3D] = useState(false);
  const [productCameraPreset, setProductCameraPreset] = useState('perspective');
  const productModelViewerRef = useRef(null);
  
  // 3D Model Parts & Material Assignment
  const [modelParts, setModelParts] = useState([]); // Array of { name, originalMaterial, assignedMaterial }
  const [showPartsPanel, setShowPartsPanel] = useState(true);
  
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [viewMode, setViewMode] = useState('grid');
  
  // Upload mode for all tabs - PERSIST per tab
  const [useUpload, setUseUpload] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageData, setUploadedImageData] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzedPrompt, setAnalyzedPrompt] = useState('');
  
  // Per-tab upload storage to persist across tab switches
  const [tabUploads, setTabUploads] = useState({
    exterior: { image: null, data: null, prompt: '' },
    interior: { image: null, data: null, prompt: '' },
    render: { image: null, data: null, prompt: '' },
    landscape: { image: null, data: null, prompt: '' },
  });
  
  // Product upload
  const [productImage, setProductImage] = useState(null);
  const [productImageData, setProductImageData] = useState(null);

  // Video creation state
  const [creatingVideo, setCreatingVideo] = useState(null);
  
  const fileInputRef = useRef(null);
  const productFileInputRef = useRef(null);
  const product3DFileInputRef = useRef(null);

  // Analyze uploaded image and suggest prompt (with timeout)
  const analyzeImage = async (base64Data) => {
    setIsAnalyzing(true);
    try {
      // Create abort controller with 15 second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await api.post('/api/images/analyze', { 
        image_data: base64Data,
        context: activeTab 
      }, { signal: controller.signal });
      
      clearTimeout(timeoutId);
      
      if (response.data) {
        setAnalyzedPrompt(response.data.suggested_prompt || '');
        setPrompt(response.data.suggested_prompt || '');
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Image analysis timed out - skipping');
      } else {
        console.error('Error analyzing image:', error);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Transfer a generated image to another tab for further editing
  const transferImageToTab = (imageUrl, targetTab) => {
    // Extract base64 data from the image URL
    const base64Data = imageUrl.startsWith('data:') 
      ? imageUrl.split(',')[1] 
      : null;
    
    if (base64Data) {
      // Set the image as uploaded for the target tab
      setTabUploads(prev => ({
        ...prev,
        [targetTab]: { image: imageUrl, data: base64Data, prompt: '' }
      }));
      
      // Switch to the target tab if different
      if (targetTab !== activeTab) {
        setActiveTab(targetTab);
      }
      
      // Enable upload mode for that tab
      setUseUpload(true);
      setUploadedImage(imageUrl);
      setUploadedImageData(base64Data);
      
      // Auto-analyze the image for the new context
      analyzeImage(base64Data);
    }
  };

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setUploadedImage(dataUrl);
        const base64Data = dataUrl.split(',')[1];
        setUploadedImageData(base64Data);
        // Auto-analyze the uploaded image
        analyzeImage(base64Data);
      };
      reader.readAsDataURL(file);
    }
  }, [activeTab]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setUploadedImage(dataUrl);
        const base64Data = dataUrl.split(',')[1];
        setUploadedImageData(base64Data);
        // Auto-analyze the uploaded image
        analyzeImage(base64Data);
      };
      reader.readAsDataURL(file);
    }
  }, [activeTab]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const clearUploadedImage = () => {
    setUploadedImage(null);
    setUploadedImageData(null);
    setAnalyzedPrompt('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Product image handlers
  const handleProductFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setProductImage(dataUrl);
        const base64Data = dataUrl.split(',')[1];
        setProductImageData(base64Data);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleProductDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        setProductImage(dataUrl);
        const base64Data = dataUrl.split(',')[1];
        setProductImageData(base64Data);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const clearProductImage = () => {
    setProductImage(null);
    setProductImageData(null);
    if (productFileInputRef.current) {
      productFileInputRef.current.value = '';
    }
  };

  // Handle 3D file upload for products (jewelry, watches, etc.)
  const handleProduct3DFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const clientFormats = ['fbx', 'obj', 'gltf', 'glb'];
    const serverFormats = ['stl', 'dae', '3ds', 'ply', 'skp', 'step', 'stp', 'ifc'];
    const allValidFormats = [...clientFormats, ...serverFormats];
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (!allValidFormats.includes(extension)) {
      alert(`Unsupported format. Supported: ${allValidFormats.join(', ').toUpperCase()}`);
      return;
    }

    if (file.size > 200 * 1024 * 1024) {
      alert('File size must be less than 200MB');
      return;
    }

    // Store the file for later use
    setProduct3DFile(file);
    const objectUrl = URL.createObjectURL(file);
    setProduct3DUrl(objectUrl);
    
    // Auto-populate prompt based on file name
    const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
    if (!prompt.trim()) {
      setPrompt(`${cleanName} product visualization`);
    }
    
    if (clientFormats.includes(extension)) {
      // Client-side conversion for FBX, OBJ, GLTF, GLB
      if (extension === 'glb' || extension === 'gltf') {
        setProductGlbUrl(objectUrl);
        setModelParts([{ name: 'Entire Model', originalMaterial: 'Mixed', assignedMaterial: 'none' }]);
      } else {
        setIsConverting3D(true);
        try {
          const { glbUrl, parts } = await convertToGLBWithParts(file, extension);
          setProductGlbUrl(glbUrl);
          setModelParts(parts);
        } catch (err) {
          console.error('Error converting 3D file:', err);
          alert('Error loading 3D file. Please try a different format.');
        } finally {
          setIsConverting3D(false);
        }
      }
    } else {
      // Server-side conversion for SKP, STL, DAE, 3DS, etc.
      setIsConverting3D(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/api/models/convert', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 360000
        });
        
        if (response.data.status === 'completed' && response.data.glb_url) {
          const glbFullUrl = `${API_URL}${response.data.glb_url}`;
          setProductGlbUrl(glbFullUrl);
          setModelParts([{ name: 'Entire Model', originalMaterial: 'Mixed', assignedMaterial: 'none' }]);
        } else {
          alert(response.data.message || 'Server conversion completed. For best results with SKP files, use the DWG/CAD tab.');
        }
      } catch (err) {
        console.error('Server conversion error:', err);
        alert('Error converting 3D file. Try exporting from your 3D software as FBX or GLB.');
      } finally {
        setIsConverting3D(false);
      }
    }
  }, [prompt]);

  // Convert FBX/OBJ to GLB format and extract model parts
  const convertToGLBWithParts = async (file, extension) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          let object;
          const arrayBuffer = event.target.result;
          
          if (extension === 'fbx') {
            const loader = new FBXLoader();
            object = loader.parse(arrayBuffer);
          } else if (extension === 'obj') {
            const loader = new OBJLoader();
            const text = new TextDecoder().decode(arrayBuffer);
            object = loader.parse(text);
          }
          
          if (!object) {
            throw new Error('Failed to parse 3D file');
          }
          
          // Extract parts (meshes) from the model
          const parts = [];
          const seenNames = new Set();
          
          object.traverse((child) => {
            if (child.isMesh) {
              // Get a unique name for this part
              let partName = child.name || `Part ${parts.length + 1}`;
              
              // Handle duplicate names
              if (seenNames.has(partName)) {
                partName = `${partName} (${parts.length + 1})`;
              }
              seenNames.add(partName);
              
              // Try to identify the original material
              let originalMaterial = 'Unknown';
              if (child.material) {
                if (Array.isArray(child.material)) {
                  originalMaterial = child.material.map(m => m.name || 'Material').join(', ');
                } else {
                  originalMaterial = child.material.name || 'Material';
                  
                  // Try to guess material type from color
                  if (child.material.color) {
                    const color = child.material.color;
                    const r = color.r, g = color.g, b = color.b;
                    
                    // Gold-ish (yellow/orange)
                    if (r > 0.7 && g > 0.5 && b < 0.4) {
                      originalMaterial = 'Gold-like';
                    }
                    // Silver/White
                    else if (r > 0.8 && g > 0.8 && b > 0.8) {
                      originalMaterial = 'Silver/White';
                    }
                    // Clear/Transparent (diamond)
                    else if (child.material.transparent || child.material.opacity < 1) {
                      originalMaterial = 'Transparent (Gem)';
                    }
                  }
                }
              }
              
              parts.push({
                name: partName,
                originalMaterial: originalMaterial,
                assignedMaterial: 'none' // User will select this
              });
            }
          });
          
          // If no parts found, add a default
          if (parts.length === 0) {
            parts.push({ name: 'Entire Model', originalMaterial: 'Mixed', assignedMaterial: 'none' });
          }
          
          // Export to GLB
          const exporter = new GLTFExporter();
          exporter.parse(
            object,
            (gltf) => {
              const blob = new Blob([gltf], { type: 'model/gltf-binary' });
              const url = URL.createObjectURL(blob);
              resolve({ glbUrl: url, parts: parts });
            },
            (error) => reject(error),
            { binary: true }
          );
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Update material assignment for a specific part
  const updatePartMaterial = (partIndex, materialId) => {
    setModelParts(prev => prev.map((part, idx) => 
      idx === partIndex ? { ...part, assignedMaterial: materialId } : part
    ));
  };

  const clearProduct3DFile = () => {
    if (product3DUrl) {
      URL.revokeObjectURL(product3DUrl);
    }
    if (productGlbUrl && productGlbUrl !== product3DUrl) {
      URL.revokeObjectURL(productGlbUrl);
    }
    setProduct3DFile(null);
    setProduct3DUrl(null);
    setProductGlbUrl(null);
    setModelParts([]);
    if (product3DFileInputRef.current) {
      product3DFileInputRef.current.value = '';
    }
  };

  // Create scene video from generated image
  const handleCreateSceneVideo = (imageUrl, imagePrompt) => {
    // Navigate to video page with the image context
    const videoPrompt = `Cinematic walkthrough scene: ${imagePrompt}. Smooth camera movement, professional architectural visualization, 4K quality`;
    navigate('/dashboard/videos', { 
      state: { 
        fromImage: true, 
        imageUrl: imageUrl,
        suggestedPrompt: videoPrompt 
      } 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let finalPrompt = '';
    
    switch (activeTab) {
      case 'exterior': {
        const style = EXTERIOR_STYLES.find(s => s.id === selectedExteriorStyle);
        finalPrompt = `${prompt}, ${style?.prompt || ''}, architectural exterior render, professional visualization, 8k quality`;
        break;
      }
      case 'interior': {
        const style = INTERIOR_STYLES.find(s => s.id === selectedInteriorStyle);
        const room = ROOM_TYPES.find(r => r.id === selectedRoomType);
        finalPrompt = `${prompt}, ${style?.prompt || ''}, ${room?.prompt || ''}, professional interior render, 8k quality`;
        break;
      }
      case 'render': {
        const style = RENDER_STYLES.find(s => s.id === selectedRenderStyle);
        const view = VIEW_TYPES.find(v => v.id === selectedViewType);
        const light = LIGHTING_CONDITIONS.find(l => l.id === selectedLighting);
        finalPrompt = `${prompt}, ${style?.prompt || ''}, ${view?.prompt || ''}, ${light?.prompt || ''}, professional architectural visualization`;
        break;
      }
      case 'landscape': {
        const style = LANDSCAPE_STYLES.find(s => s.id === selectedLandscapeStyle);
        finalPrompt = `${prompt}, ${style?.prompt || ''}, landscape architecture render, professional visualization, 8k quality`;
        break;
      }
      case 'product': {
        // Build comprehensive product prompt with all customization options
        const style = PRODUCT_STYLES.find(s => s.id === selectedProductStyle);
        const category = PRODUCT_CATEGORIES.find(c => c.id === selectedProductCategory);
        const material = PRODUCT_MATERIALS.find(m => m.id === selectedProductMaterial);
        const background = PRODUCT_BACKGROUNDS.find(b => b.id === selectedProductBackground);
        const lighting = PRODUCT_LIGHTING.find(l => l.id === selectedProductLighting);
        
        // Build the full prompt with all options
        let productPromptParts = [];
        
        // Category context
        if (category && category.id !== 'jewelry') {
          productPromptParts.push(`${category.name} product visualization`);
        } else {
          productPromptParts.push('Jewelry product visualization');
        }
        
        // User prompt
        if (prompt.trim()) {
          productPromptParts.push(prompt.trim());
        }
        
        // Style
        if (style?.prompt) {
          productPromptParts.push(style.prompt);
        }
        
        // Material/Finish - Global or Part-specific
        if (product3DMode && modelParts.length > 0) {
          // Build part-specific material instructions
          const partsWithMaterials = modelParts.filter(p => p.assignedMaterial !== 'none');
          if (partsWithMaterials.length > 0) {
            const partInstructions = partsWithMaterials.map(part => {
              const materialPreset = PRODUCT_MATERIALS.find(m => m.id === part.assignedMaterial);
              return `${part.name}: ${materialPreset?.prompt || part.assignedMaterial}`;
            }).join('; ');
            productPromptParts.push(`PART-SPECIFIC MATERIALS: ${partInstructions}`);
          }
        } else if (material && material.id !== 'none' && material.prompt) {
          productPromptParts.push(`MATERIAL: ${material.prompt}`);
        }
        
        // Background
        if (background && background.prompt) {
          productPromptParts.push(`BACKGROUND: ${background.prompt}`);
        }
        
        // Lighting
        if (lighting?.prompt) {
          productPromptParts.push(`LIGHTING: ${lighting.prompt}`);
        }
        
        // Camera angle for 3D
        if (product3DMode && productGlbUrl) {
          const cameraPreset = PRODUCT_CAMERA_PRESETS.find(p => p.id === productCameraPreset);
          if (cameraPreset) {
            productPromptParts.push(`CAMERA: ${cameraPreset.name} angle`);
          }
        }
        
        productPromptParts.push('professional product photography, high-end catalog quality, 8K resolution');
        
        const fullProductPrompt = productPromptParts.join('. ');
        
        // Handle 3D model - capture screenshot and send for AI rendering
        if (product3DMode && productGlbUrl && productModelViewerRef.current) {
          try {
            // Capture screenshot from model-viewer
            const modelViewer = productModelViewerRef.current;
            
            // Wait a moment for the model to render
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Get the canvas and convert to base64
            const blob = await modelViewer.toBlob({ idealAspect: true });
            const reader = new FileReader();
            
            reader.onload = async () => {
              const base64 = reader.result.split(',')[1];
              try {
                await onReTheme(fullProductPrompt, base64, `product-3d-${selectedProductStyle}`);
                setPrompt('');
              } catch (err) {
                console.error('Product 3D retheme error:', err);
                alert('Failed to generate render. Please try again.');
              }
            };
            reader.readAsDataURL(blob);
          } catch (err) {
            console.error('Product 3D capture error:', err);
            alert('Failed to capture 3D model. Please try again.');
          }
          return;
        } else if (!productImageData && !useUpload) {
          // Generate new product image from text only
          finalPrompt = fullProductPrompt;
        } else if (productImageData) {
          // Transform uploaded product image
          try {
            await onReTheme(fullProductPrompt, productImageData, `product-${selectedProductStyle}`);
            setPrompt('');
            clearProductImage();
          } catch (err) {
            console.error('Product retheme error:', err);
          }
          return;
        }
        break;
      }
      default:
        finalPrompt = prompt;
    }

    if (useUpload && uploadedImageData) {
      await onReTheme(finalPrompt, uploadedImageData, activeTab);
      clearUploadedImage();
    } else {
      if (!prompt.trim() && activeTab !== 'product') return;
      onGenerate(finalPrompt);
    }
    setPrompt('');
  };

  const getTabColor = () => {
    switch (activeTab) {
      case 'exterior': return 'blue';
      case 'interior': return 'amber';
      case 'render': return 'emerald';
      case 'landscape': return 'green';
      case 'product': return 'purple';
      default: return 'blue';
    }
  };

  const getTabGradient = () => {
    switch (activeTab) {
      case 'exterior': return 'from-blue-600 to-cyan-600';
      case 'interior': return 'from-amber-600 to-orange-600';
      case 'render': return 'from-emerald-600 to-teal-600';
      case 'landscape': return 'from-green-600 to-lime-600';
      case 'product': return 'from-purple-600 to-pink-600';
      default: return 'from-blue-600 to-cyan-600';
    }
  };

  const suggestions = {
    exterior: ['Modern glass villa with infinity pool', 'Parametric museum with flowing curves'],
    interior: ['Minimalist living room with floor-to-ceiling windows', 'Luxury penthouse with city views'],
    render: ['Sunset view of contemporary house', 'Aerial view of residential complex'],
    landscape: ['Japanese zen garden with koi pond', 'Modern rooftop terrace with city skyline'],
    product: ['Designer chair on concrete floor', 'Pendant lamp with warm ambient glow'],
  };

  // Render upload section for tabs
  const renderUploadSection = () => (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg border border-zinc-800">
        <div className="flex items-center gap-3">
          {useUpload ? (
            <Upload className="h-5 w-5 text-blue-400" />
          ) : (
            <Sparkles className="h-5 w-5 text-blue-400" />
          )}
          <div>
            <p className="text-sm font-medium text-white">
              {useUpload ? 'Transform Your Image' : 'Generate New Design'}
            </p>
            <p className="text-xs text-zinc-500">
              {useUpload ? 'Upload & re-render with AI analysis' : 'Create from description'}
            </p>
          </div>
        </div>
        <Switch
          checked={useUpload}
          onCheckedChange={(checked) => {
            setUseUpload(checked);
            if (!checked) clearUploadedImage();
          }}
        />
      </div>

      {/* Upload Area */}
      {useUpload && (
        <div 
          className={cn(
            "border-2 border-dashed rounded-xl p-4 text-center transition-all",
            uploadedImage 
              ? "border-blue-500 bg-blue-500/10" 
              : "border-zinc-700 hover:border-zinc-500 cursor-pointer"
          )}
          onClick={() => !uploadedImage && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          {uploadedImage ? (
            <div className="space-y-3">
              <div className="relative inline-block">
                <img 
                  src={uploadedImage} 
                  alt="Upload" 
                  className="max-h-40 rounded-lg mx-auto"
                />
                <Button
                  size="icon"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearUploadedImage();
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              
              {isAnalyzing && (
                <div className="flex items-center justify-center gap-2 text-sm text-zinc-400">
                  <Scan className="h-4 w-4 animate-pulse" />
                  Analyzing image...
                </div>
              )}
              
              {analyzedPrompt && !isAnalyzing && (
                <div className="bg-zinc-800/50 rounded-lg p-3 text-left">
                  <p className="text-xs text-zinc-500 mb-1">AI Suggested Prompt:</p>
                  <p className="text-sm text-zinc-300">{analyzedPrompt}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              <Upload className="h-10 w-10 text-blue-500/50 mx-auto mb-2" />
              <p className="text-sm text-zinc-300 mb-1">Drop image or click to upload</p>
              <p className="text-xs text-zinc-500">AI will analyze and suggest improvements</p>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-white" />
              </div>
              Design Studio
            </h1>
            <p className="text-sm text-zinc-400 mt-1">AI-Powered Architectural & Design Visualization</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              className="h-8 w-8"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'large' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('large')}
              className="h-8 w-8"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Generator Panel */}
        <div className={cn(
          "border-b lg:border-b-0 lg:border-r border-zinc-800 overflow-y-auto",
          (activeTab === '3dmodel' || activeTab === 'planrender') ? "lg:w-full" : "lg:w-[480px]"
        )}>
          <Tabs value={activeTab} onValueChange={(v) => { 
            // Save current tab's upload state before switching
            if (activeTab !== '3dmodel' && activeTab !== 'product') {
              setTabUploads(prev => ({
                ...prev,
                [activeTab]: { 
                  image: uploadedImage, 
                  data: uploadedImageData, 
                  prompt: analyzedPrompt 
                }
              }));
            }
            
            // Switch to new tab
            setActiveTab(v);
            
            // Restore new tab's upload state (if it had one)
            if (v !== '3dmodel' && v !== 'product') {
              const savedUpload = tabUploads[v];
              if (savedUpload && savedUpload.image) {
                setUploadedImage(savedUpload.image);
                setUploadedImageData(savedUpload.data);
                setAnalyzedPrompt(savedUpload.prompt);
                setUseUpload(true);
              } else {
                setUploadedImage(null);
                setUploadedImageData(null);
                setAnalyzedPrompt('');
                setUseUpload(false);
              }
            } else {
              setUseUpload(false);
            }
          }} className="w-full">
            <div className="sticky top-0 bg-zinc-950 z-10 p-4 pb-0">
              <TabsList className="w-full grid grid-cols-7 bg-zinc-900 h-auto p-1">
                <TabsTrigger value="exterior" className="data-[state=active]:bg-blue-600 text-xs py-2 flex flex-col gap-1">
                  <Building2 className="h-4 w-4" />
                  <span>Exterior</span>
                </TabsTrigger>
                <TabsTrigger value="interior" className="data-[state=active]:bg-amber-600 text-xs py-2 flex flex-col gap-1">
                  <Sofa className="h-4 w-4" />
                  <span>Interior</span>
                </TabsTrigger>
                <TabsTrigger value="render" className="data-[state=active]:bg-emerald-600 text-xs py-2 flex flex-col gap-1">
                  <Box className="h-4 w-4" />
                  <span>3D Render</span>
                </TabsTrigger>
                <TabsTrigger value="3dmodel" className="data-[state=active]:bg-cyan-600 text-xs py-2 flex flex-col gap-1">
                  <Package className="h-4 w-4" />
                  <span>3D Model</span>
                </TabsTrigger>
                <TabsTrigger value="planrender" className="data-[state=active]:bg-orange-600 text-xs py-2 flex flex-col gap-1">
                  <FileText className="h-4 w-4" />
                  <span>DWG/CAD</span>
                </TabsTrigger>
                <TabsTrigger value="landscape" className="data-[state=active]:bg-green-600 text-xs py-2 flex flex-col gap-1">
                  <TreePine className="h-4 w-4" />
                  <span>Landscape</span>
                </TabsTrigger>
                <TabsTrigger value="product" className="data-[state=active]:bg-purple-600 text-xs py-2 flex flex-col gap-1">
                  <ShoppingBag className="h-4 w-4" />
                  <span>Product</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* 3D Model Tab - Full Width with Gallery */}
            {activeTab === '3dmodel' && (
              <div className="flex flex-col h-full relative">
                {/* Loading overlay when generating */}
                {isGenerating && (
                  <div className="absolute inset-0 bg-black/70 z-50 flex items-center justify-center">
                    <div className="bg-zinc-900 rounded-xl p-8 text-center border border-cyan-500/50 shadow-2xl">
                      <Loader2 className="h-12 w-12 animate-spin text-cyan-400 mx-auto mb-4" />
                      <h3 className="text-white text-lg font-semibold mb-2">🎨 Generating AI Render...</h3>
                      <p className="text-zinc-400 text-sm">Applying photorealistic materials to your 3D model</p>
                      <p className="text-zinc-500 text-xs mt-2">This may take 30-60 seconds</p>
                    </div>
                  </div>
                )}
                
                {/* 3D Viewer */}
                <div className="h-[500px] flex-shrink-0">
                  <Suspense fallback={
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
                    </div>
                  }>
                    <ModelViewer3D 
                      onRenderCapture={(dataUrl, fileName) => {
                        console.log('Render captured:', fileName);
                      }}
                      onStyleWithAI={async (base64, description, renderEngine) => {
                        // Use the captured image for AI styling and add to local gallery
                        console.log('Starting AI Render with prompt:', description);
                        console.log('Render engine:', renderEngine);
                        try {
                          const result = await onReTheme(description, base64, '3dmodel', renderEngine);
                          // Add the result to model3DRenders gallery
                          if (result) {
                            console.log('AI Render completed successfully');
                            console.log('Result object:', { 
                              id: result.id, 
                              hasImageUrl: !!result.imageUrl,
                              imageUrlLength: result.imageUrl?.length,
                              imageUrlPrefix: result.imageUrl?.substring(0, 50)
                            });
                            setModel3DRenders(prev => {
                              const newRenders = [result, ...prev];
                              console.log('Gallery updated, total renders:', newRenders.length);
                              return newRenders;
                            });
                          } else {
                            console.warn('AI Render returned null/undefined result');
                          }
                          return result;
                        } catch (err) {
                          console.error('AI Render error:', err);
                          throw err; // Re-throw to show error in ModelViewer3D
                        }
                      }}
                      generatedImages={model3DRenders}
                      onImageGenerated={(newImage) => {
                        setModel3DRenders(prev => [newImage, ...prev]);
                      }}
                      // Persist model file across tab switches
                      persistedFile={model3DFile}
                      persistedUrl={model3DUrl}
                      persistedForgeUrn={model3DForgeUrn}
                      persistedForgeStatus={model3DForgeStatus}
                      onModelLoad={(file, url) => {
                        setModel3DFile(file);
                        setModel3DUrl(url);
                      }}
                      onForgeUrnChange={(urn, status) => {
                        setModel3DForgeUrn(urn);
                        setModel3DForgeStatus(status);
                      }}
                    />
                  </Suspense>
                </div>
                
                {/* 3D Model Renders Gallery - Always visible in 3D tab */}
                <div className="mt-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Grid className="h-5 w-5 text-cyan-400" />
                    <h3 className="text-white font-medium">Your 3D Renders</h3>
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                      {model3DRenders.length} renders
                    </span>
                  </div>
                  {model3DRenders.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500 text-sm">
                      <p>No renders yet. Upload a 3D model and click AI Render to generate.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto">
                      {/* Show local renders first */}
                      {model3DRenders.map((img, idx) => (
                        <Dialog key={`local-${idx}`}>
                          <DialogTrigger asChild>
                            <div className="relative group cursor-pointer rounded-lg overflow-hidden aspect-square bg-zinc-800">
                              {img.imageUrl ? (
                                <img 
                                  src={img.imageUrl} 
                                  alt={`3D Render ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  loading="eager"
                                  decoding="async"
                                  onLoad={() => console.log(`Image ${idx + 1} loaded successfully`)}
                                  onError={(e) => {
                                    console.error('Image load error for render', idx + 1, ':', e.target.src?.substring(0, 50));
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-zinc-500 text-xs">Load Error</div>';
                                  }}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full text-zinc-500 text-xs">No Image</div>
                              )}
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <ZoomIn className="h-5 w-5 text-white" />
                              </div>
                            </div>
                          </DialogTrigger>
                          <DialogContent className="max-w-[95vw] sm:max-w-4xl bg-zinc-900 border-zinc-800 p-2 sm:p-4">
                            <div className="flex items-center justify-center">
                              <img 
                                src={img.imageUrl} 
                                alt="AI Rendered 3D Model" 
                                className="max-w-full max-h-[70vh] object-contain rounded-lg"
                              />
                            </div>
                            <div className="mt-3 flex justify-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = img.imageUrl;
                                  link.download = `3d-render-${idx}.png`;
                                  link.click();
                                }}
                                className="bg-cyan-600 hover:bg-cyan-700"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download Image
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      ))}
                      {/* Show server-side 3D renders */}
                      {images.filter(img => 
                        img.prompt?.toLowerCase().includes('3d') || 
                        img.prompt?.toLowerCase().includes('re-themed') ||
                        img.prompt?.toLowerCase().includes('3d-model') ||
                        img.prompt?.toLowerCase().includes('[3d-model]')
                      ).map((img) => (
                        <Dialog key={img.id}>
                          <DialogTrigger asChild>
                            <div className="relative group cursor-pointer rounded-lg overflow-hidden aspect-square bg-zinc-800">
                              <img 
                                src={img.imageUrl} 
                                alt="3D Render" 
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <ZoomIn className="h-5 w-5 text-white" />
                              </div>
                            </div>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-4">
                            <div className="flex items-center justify-center">
                              <img 
                                src={img.imageUrl} 
                                alt="AI Rendered 3D Model" 
                                className="max-w-full max-h-[75vh] object-contain rounded-lg"
                              />
                            </div>
                            <div className="mt-3 flex justify-end">
                              <Button
                                size="sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = img.imageUrl;
                                  link.download = `3d-render-${img.id}.png`;
                                  link.click();
                                }}
                                className="bg-cyan-600 hover:bg-cyan-700"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download Image
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Plan Render Tab (DWG/CAD) - Full Width */}
            {activeTab === 'planrender' && (
              <div className="flex flex-col h-full">
                <Suspense fallback={
                  <div className="flex items-center justify-center h-96">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                }>
                  <PlanRenderTab onRenderWithAI={onReTheme} />
                </Suspense>
              </div>
            )}

            <div className={cn("p-4 space-y-4", (activeTab === '3dmodel' || activeTab === 'planrender') && "hidden")}>
              {/* ===== EXTERIOR TAB ===== */}
              {activeTab === 'exterior' && (
                <>
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-blue-300 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Exterior Architecture Visualization
                    </p>
                  </div>
                  
                  {renderUploadSection()}
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">
                      {useUpload ? 'Describe the transformation' : 'Describe Your Vision'}
                    </Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={useUpload 
                        ? "Transform to modern style with large windows..." 
                        : "A modern minimalist house with large glass windows overlooking the ocean..."}
                      className="min-h-[80px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 mb-2 block">Architecture Style</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {EXTERIOR_STYLES.map((style) => {
                        const IconComponent = style.icon;
                        return (
                          <button
                            key={style.id}
                            type="button"
                            onClick={() => setSelectedExteriorStyle(style.id)}
                            className={cn(
                              "p-2 rounded-lg border text-center transition-all",
                              selectedExteriorStyle === style.id
                                ? "border-blue-500 bg-blue-500/20 text-white"
                                : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                            )}
                          >
                            <IconComponent className="h-5 w-5 mx-auto" />
                            <p className="text-xs mt-1 truncate">{style.name}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* ===== INTERIOR TAB ===== */}
              {activeTab === 'interior' && (
                <>
                  <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <p className="text-sm text-amber-300 flex items-center gap-2">
                      <Sofa className="h-4 w-4" />
                      Interior Design Visualization
                    </p>
                  </div>
                  
                  {renderUploadSection()}
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">
                      {useUpload ? 'Describe the transformation' : 'Describe Your Space'}
                    </Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={useUpload 
                        ? "Redesign in Scandinavian style with warmer tones..." 
                        : "A cozy Scandinavian living room with natural wood furniture and warm textiles..."}
                      className="min-h-[80px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 mb-2 block">Interior Style</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {INTERIOR_STYLES.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setSelectedInteriorStyle(style.id)}
                          className={cn(
                            "p-2 rounded-lg border text-center transition-all",
                            selectedInteriorStyle === style.id
                              ? "border-amber-500 bg-amber-500/20 text-white"
                              : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                          )}
                        >
                          <span className="text-lg">{style.icon}</span>
                          <p className="text-xs mt-1 truncate">{style.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-zinc-300 mb-2 block">Room Type</Label>
                    <Select value={selectedRoomType} onValueChange={setSelectedRoomType}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {ROOM_TYPES.map((room) => (
                          <SelectItem key={room.id} value={room.id} className="text-zinc-300">
                            {room.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* ===== 3D RENDER TAB ===== */}
              {activeTab === 'render' && (
                <>
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                    <p className="text-sm text-emerald-300 flex items-center gap-2">
                      <Box className="h-4 w-4" />
                      Professional 3D Rendering
                    </p>
                  </div>
                  
                  {renderUploadSection()}
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">
                      {useUpload ? 'Describe the re-render' : 'Describe Your Scene'}
                    </Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={useUpload 
                        ? "Re-render with V-Ray quality and golden hour lighting..." 
                        : "A contemporary villa at sunset with dramatic shadows and warm lighting..."}
                      className="min-h-[80px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 mb-2 block">Render Engine Style</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {RENDER_STYLES.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setSelectedRenderStyle(style.id)}
                          className={cn(
                            "p-2 rounded-lg border text-center transition-all",
                            selectedRenderStyle === style.id
                              ? "border-emerald-500 bg-emerald-500/20 text-white"
                              : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                          )}
                        >
                          <span className="text-lg">{style.icon}</span>
                          <p className="text-xs mt-1 truncate">{style.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-300 mb-2 block">View Type</Label>
                      <Select value={selectedViewType} onValueChange={setSelectedViewType}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {VIEW_TYPES.map((view) => (
                            <SelectItem key={view.id} value={view.id} className="text-zinc-300">
                              {view.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-300 mb-2 block">Lighting</Label>
                      <Select value={selectedLighting} onValueChange={setSelectedLighting}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {LIGHTING_CONDITIONS.map((light) => (
                            <SelectItem key={light.id} value={light.id} className="text-zinc-300">
                              {light.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {/* ===== LANDSCAPE TAB ===== */}
              {activeTab === 'landscape' && (
                <>
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-sm text-green-300 flex items-center gap-2">
                      <TreePine className="h-4 w-4" />
                      Landscape Architecture
                    </p>
                  </div>
                  
                  {renderUploadSection()}
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">
                      {useUpload ? 'Describe the transformation' : 'Describe Your Landscape'}
                    </Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={useUpload 
                        ? "Transform to Japanese zen garden style..." 
                        : "A serene Japanese zen garden with raked gravel, stone lanterns, and a koi pond..."}
                      className="min-h-[80px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 mb-2 block">Landscape Style</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {LANDSCAPE_STYLES.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setSelectedLandscapeStyle(style.id)}
                          className={cn(
                            "p-2 rounded-lg border text-center transition-all",
                            selectedLandscapeStyle === style.id
                              ? "border-green-500 bg-green-500/20 text-white"
                              : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                          )}
                        >
                          <span className="text-lg">{style.icon}</span>
                          <p className="text-xs mt-1 truncate">{style.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ===== PRODUCT TAB - ENHANCED ===== */}
              {activeTab === 'product' && (
                <>
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <p className="text-sm text-purple-300 flex items-center gap-2">
                      <Gem className="h-4 w-4" />
                      Product Visualization Studio
                    </p>
                    <p className="text-xs text-purple-400/70 mt-1">Jewelry, Watches, Electronics, Fashion & More</p>
                  </div>
                  
                  {/* Upload Mode Toggle - Image vs 3D */}
                  <div className="flex gap-2 p-1 bg-zinc-900 rounded-lg">
                    <button
                      onClick={() => setProduct3DMode(false)}
                      className={cn(
                        "flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-2",
                        !product3DMode 
                          ? "bg-purple-500/20 text-purple-400 border border-purple-500/50" 
                          : "text-zinc-400 hover:text-zinc-300"
                      )}
                    >
                      <ImageIcon className="h-3 w-3" />
                      Image Upload
                    </button>
                    <button
                      onClick={() => setProduct3DMode(true)}
                      className={cn(
                        "flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-2",
                        product3DMode 
                          ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50" 
                          : "text-zinc-400 hover:text-zinc-300"
                      )}
                    >
                      <Box className="h-3 w-3" />
                      3D Model (FBX/OBJ)
                    </button>
                  </div>

                  {/* Product Category Selection */}
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Product Category</Label>
                    <div className="grid grid-cols-5 gap-1.5">
                      {PRODUCT_CATEGORIES.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setSelectedProductCategory(cat.id)}
                          className={cn(
                            "p-1.5 rounded-lg border text-center transition-all",
                            selectedProductCategory === cat.id
                              ? "border-purple-500 bg-purple-500/20 text-white"
                              : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                          )}
                          title={cat.description}
                        >
                          <span className="text-base">{cat.icon}</span>
                          <p className="text-[9px] mt-0.5 truncate">{cat.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Product Upload Area */}
                  {!product3DMode ? (
                    <div 
                      className={cn(
                        "border-2 border-dashed rounded-xl p-4 text-center transition-all",
                        productImage 
                          ? "border-purple-500 bg-purple-500/10" 
                          : "border-zinc-700 hover:border-purple-500/50 hover:bg-zinc-900/50 cursor-pointer"
                      )}
                      onClick={() => !productImage && productFileInputRef.current?.click()}
                      onDrop={handleProductDrop}
                      onDragOver={handleDragOver}
                    >
                      <input
                        ref={productFileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleProductFileUpload}
                        className="hidden"
                      />
                      
                      {productImage ? (
                        <div className="relative inline-block">
                          <img 
                            src={productImage} 
                            alt="Product" 
                            className="max-h-32 rounded-lg mx-auto"
                          />
                          <Button
                            size="icon"
                            variant="destructive"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearProductImage();
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <ShoppingBag className="h-8 w-8 text-purple-500/50 mx-auto mb-2" />
                          <p className="text-sm text-zinc-300 mb-1">Upload product image (optional)</p>
                          <p className="text-xs text-zinc-500">PNG, JPG, WEBP • Max 10MB</p>
                        </>
                      )}
                    </div>
                  ) : (
                    // 3D Model Upload Area
                    <div className="space-y-3">
                      {/* Upload Button or Model Viewer */}
                      {!productGlbUrl ? (
                        <div 
                          className={cn(
                            "border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer",
                            isConverting3D
                              ? "border-cyan-500 bg-cyan-500/10"
                              : "border-cyan-700 hover:border-cyan-500/50 bg-cyan-500/5"
                          )}
                          onClick={() => !isConverting3D && product3DFileInputRef.current?.click()}
                        >
                          <input
                            ref={product3DFileInputRef}
                            type="file"
                            accept=".fbx,.obj,.gltf,.glb,.stl,.dae,.3ds,.ply,.skp,.step,.stp,.ifc"
                            onChange={handleProduct3DFileUpload}
                            className="hidden"
                          />
                          
                          {isConverting3D ? (
                            <div className="py-4">
                              <Loader2 className="h-8 w-8 text-cyan-400 mx-auto mb-2 animate-spin" />
                              <p className="text-sm text-cyan-400">Converting 3D model...</p>
                            </div>
                          ) : (
                            <>
                              <Box className="h-8 w-8 text-cyan-500/50 mx-auto mb-2" />
                              <p className="text-sm text-zinc-300 mb-1">Upload 3D Product Model</p>
                              <p className="text-xs text-zinc-500 mb-2">FBX, OBJ, SKP, STL, DAE, GLTF, GLB + more</p>
                              <div className="flex flex-wrap justify-center gap-2">
                                {['FBX', 'OBJ', 'SKP', 'STL', 'GLTF', 'GLB'].map(format => (
                                  <span key={format} className="px-2 py-0.5 text-[10px] bg-zinc-800 text-cyan-400 rounded">
                                    .{format.toLowerCase()}
                                  </span>
                                ))}
                              </div>
                              <p className="text-[10px] text-zinc-400 mt-2">Max 200MB</p>
                            </>
                          )}
                        </div>
                      ) : (
                        // 3D Model Viewer
                        <div className="relative">
                          {/* Model Viewer */}
                          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-xl overflow-hidden border border-cyan-500/30">
                            <model-viewer
                              ref={productModelViewerRef}
                              src={productGlbUrl}
                              camera-controls
                              auto-rotate
                              shadow-intensity="1"
                              exposure="1"
                              environment-image="neutral"
                              camera-orbit={PRODUCT_CAMERA_PRESETS.find(p => p.id === productCameraPreset)?.orbit || '45deg 65deg 105%'}
                              style={{ width: '100%', height: '200px', backgroundColor: 'transparent' }}
                            />
                          </div>
                          
                          {/* File Info & Controls */}
                          <div className="flex items-center justify-between mt-2 px-1">
                            <div className="flex items-center gap-2">
                              <Gem className="h-4 w-4 text-cyan-400" />
                              <span className="text-xs text-zinc-300 truncate max-w-[120px]">{product3DFile?.name}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={clearProduct3DFile}
                            >
                              <X className="h-3 w-3 mr-1" />
                              Remove
                            </Button>
                          </div>
                          
                          {/* Camera Presets */}
                          <div className="mt-2">
                            <Label className="text-zinc-400 text-[10px] mb-1.5 block">Camera Angle</Label>
                            <div className="grid grid-cols-6 gap-1">
                              {PRODUCT_CAMERA_PRESETS.map((preset) => (
                                <button
                                  key={preset.id}
                                  onClick={() => {
                                    setProductCameraPreset(preset.id);
                                    if (productModelViewerRef.current) {
                                      productModelViewerRef.current.cameraOrbit = preset.orbit;
                                    }
                                  }}
                                  className={cn(
                                    "p-1.5 rounded border text-center transition-all",
                                    productCameraPreset === preset.id
                                      ? "border-cyan-500 bg-cyan-500/20 text-cyan-400"
                                      : "border-zinc-700 text-zinc-500 hover:border-zinc-600"
                                  )}
                                >
                                  <span className="text-sm">{preset.icon}</span>
                                  <p className="text-[8px] mt-0.5">{preset.name}</p>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Model Parts - Material Assignment */}
                          {modelParts.length > 0 && (
                            <div className="mt-3 border border-amber-500/30 rounded-lg bg-amber-500/5 p-2">
                              <button
                                onClick={() => setShowPartsPanel(!showPartsPanel)}
                                className="w-full flex items-center justify-between text-xs text-amber-400 hover:text-amber-300 mb-2"
                              >
                                <span className="flex items-center gap-2">
                                  <Layers className="h-3 w-3" />
                                  Model Parts ({modelParts.length}) - Assign Materials
                                </span>
                                <span className={cn("transition-transform text-[10px]", showPartsPanel && "rotate-180")}>▼</span>
                              </button>
                              
                              {showPartsPanel && (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {modelParts.map((part, idx) => (
                                    <div key={idx} className="bg-zinc-900/80 rounded-lg p-2 border border-zinc-700">
                                      <div className="flex items-center justify-between mb-1.5">
                                        <div>
                                          <p className="text-xs text-white font-medium">{part.name}</p>
                                          <p className="text-[10px] text-zinc-500">Original: {part.originalMaterial}</p>
                                        </div>
                                      </div>
                                      
                                      {/* Material Selection for this part */}
                                      <div className="grid grid-cols-5 gap-1">
                                        <button
                                          onClick={() => updatePartMaterial(idx, 'none')}
                                          className={cn(
                                            "p-1 rounded border text-[8px] transition-all",
                                            part.assignedMaterial === 'none'
                                              ? "border-amber-500 bg-amber-500/20 text-amber-400"
                                              : "border-zinc-700 text-zinc-500 hover:border-zinc-600"
                                          )}
                                        >
                                          Auto
                                        </button>
                                        {/* Quick material options for jewelry */}
                                        {[
                                          { id: 'gold-24k', name: '24K Gold', icon: '🥇' },
                                          { id: 'rose-gold', name: 'Rose', icon: '🌹' },
                                          { id: 'platinum', name: 'Platinum', icon: '💎' },
                                          { id: 'silver', name: 'Silver', icon: '🥈' },
                                          { id: 'diamond', name: 'Diamond', icon: '💠' },
                                          { id: 'ruby', name: 'Ruby', icon: '❤️' },
                                          { id: 'emerald', name: 'Emerald', icon: '💚' },
                                          { id: 'sapphire', name: 'Sapphire', icon: '💙' },
                                        ].map((mat) => (
                                          <button
                                            key={mat.id}
                                            onClick={() => updatePartMaterial(idx, mat.id)}
                                            className={cn(
                                              "p-1 rounded border text-[8px] transition-all flex flex-col items-center",
                                              part.assignedMaterial === mat.id
                                                ? "border-amber-500 bg-amber-500/20 text-amber-400"
                                                : "border-zinc-700 text-zinc-500 hover:border-zinc-600"
                                            )}
                                            title={mat.name}
                                          >
                                            <span>{mat.icon}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Product Description */}
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Describe Your Product</Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={selectedProductCategory === 'jewelry' 
                        ? "Diamond engagement ring with halo setting, 2 carat center stone..."
                        : selectedProductCategory === 'watches'
                        ? "Luxury chronograph watch with rose gold case and leather strap..."
                        : "A designer product on elegant display surface..."
                      }
                      className="min-h-[60px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none text-sm"
                    />
                  </div>

                  {/* Visualization Style */}
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Visualization Style</Label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {PRODUCT_STYLES.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setSelectedProductStyle(style.id)}
                          className={cn(
                            "p-1.5 rounded-lg border text-center transition-all",
                            selectedProductStyle === style.id
                              ? "border-purple-500 bg-purple-500/20 text-white"
                              : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                          )}
                        >
                          <span className="text-sm">{style.icon}</span>
                          <p className="text-[9px] mt-0.5 truncate">{style.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Material/Finish - Collapsible Panel */}
                  <div>
                    <button
                      onClick={() => setShowProductMaterialPanel(!showProductMaterialPanel)}
                      className="w-full flex items-center justify-between text-xs text-zinc-300 hover:text-white mb-2 py-1"
                    >
                      <span className="flex items-center gap-2">
                        <Gem className="h-3 w-3" />
                        Material/Finish {selectedProductMaterial !== 'none' && <span className="text-amber-400">({PRODUCT_MATERIALS.find(m => m.id === selectedProductMaterial)?.name})</span>}
                      </span>
                      <span className={cn("transition-transform text-[10px]", showProductMaterialPanel && "rotate-180")}>▼</span>
                    </button>
                    {showProductMaterialPanel && (
                      <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto pr-1 pb-2">
                        {PRODUCT_MATERIALS.map((mat) => (
                          <button
                            key={mat.id}
                            onClick={() => setSelectedProductMaterial(mat.id)}
                            className={cn(
                              "flex flex-col items-center justify-center p-1.5 rounded-lg border text-[9px] transition-all",
                              selectedProductMaterial === mat.id
                                ? "border-amber-500 bg-amber-500/20 text-amber-400"
                                : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                            )}
                          >
                            <span className="text-sm mb-0.5">{mat.icon}</span>
                            <span className="truncate w-full text-center">{mat.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Background - Collapsible Panel */}
                  <div>
                    <button
                      onClick={() => setShowProductBackgroundPanel(!showProductBackgroundPanel)}
                      className="w-full flex items-center justify-between text-xs text-zinc-300 hover:text-white mb-2 py-1"
                    >
                      <span className="flex items-center gap-2">
                        <Layers className="h-3 w-3" />
                        Background {selectedProductBackground !== 'white' && <span className="text-cyan-400">({PRODUCT_BACKGROUNDS.find(b => b.id === selectedProductBackground)?.name})</span>}
                      </span>
                      <span className={cn("transition-transform text-[10px]", showProductBackgroundPanel && "rotate-180")}>▼</span>
                    </button>
                    {showProductBackgroundPanel && (
                      <div className="grid grid-cols-5 gap-1.5 max-h-32 overflow-y-auto pr-1 pb-2">
                        {PRODUCT_BACKGROUNDS.map((bg) => (
                          <button
                            key={bg.id}
                            onClick={() => setSelectedProductBackground(bg.id)}
                            className={cn(
                              "flex flex-col items-center justify-center p-1.5 rounded-lg border text-[9px] transition-all",
                              selectedProductBackground === bg.id
                                ? "border-cyan-500 bg-cyan-500/20 text-cyan-400"
                                : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                            )}
                          >
                            <div 
                              className="w-6 h-6 rounded mb-0.5 border border-zinc-600" 
                              style={{ background: bg.color }}
                            />
                            <span className="truncate w-full text-center">{bg.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Lighting - Collapsible Panel */}
                  <div>
                    <button
                      onClick={() => setShowProductLightingPanel(!showProductLightingPanel)}
                      className="w-full flex items-center justify-between text-xs text-zinc-300 hover:text-white mb-2 py-1"
                    >
                      <span className="flex items-center gap-2">
                        <Sun className="h-3 w-3" />
                        Lighting {selectedProductLighting !== 'soft-box' && <span className="text-yellow-400">({PRODUCT_LIGHTING.find(l => l.id === selectedProductLighting)?.name})</span>}
                      </span>
                      <span className={cn("transition-transform text-[10px]", showProductLightingPanel && "rotate-180")}>▼</span>
                    </button>
                    {showProductLightingPanel && (
                      <div className="grid grid-cols-4 gap-1.5 pb-2">
                        {PRODUCT_LIGHTING.map((light) => (
                          <button
                            key={light.id}
                            onClick={() => setSelectedProductLighting(light.id)}
                            className={cn(
                              "flex flex-col items-center justify-center p-1.5 rounded-lg border text-[9px] transition-all",
                              selectedProductLighting === light.id
                                ? "border-yellow-500 bg-yellow-500/20 text-yellow-400"
                                : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                            )}
                          >
                            <span className="text-sm mb-0.5">{light.icon}</span>
                            <span className="truncate w-full text-center">{light.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Aspect Ratio - Common for all tabs */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Aspect Ratio</Label>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {ASPECT_RATIOS.map((ratio) => (
                      <SelectItem key={ratio.id} value={ratio.id} className="text-zinc-300">
                        {ratio.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={(!prompt.trim() && !useUpload && activeTab !== 'product') || (useUpload && !uploadedImageData) || isGenerating || isAnalyzing}
                className={cn(
                  "w-full h-12 font-medium bg-gradient-to-r",
                  getTabGradient(),
                  "hover:opacity-90"
                )}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {useUpload ? 'Transforming...' : 'Generating...'}
                  </>
                ) : isAnalyzing ? (
                  <>
                    <Scan className="h-5 w-5 mr-2 animate-pulse" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    {useUpload ? 'Transform Image' : `Generate ${activeTab === 'exterior' ? 'Exterior' : activeTab === 'interior' ? 'Interior' : activeTab === 'render' ? '3D Render' : activeTab === 'landscape' ? 'Landscape' : 'Product'}`}
                  </>
                )}
              </Button>

              {/* Quick Suggestions */}
              {!useUpload && (
                <div className="pt-2">
                  <p className="text-xs text-zinc-500 mb-2">Quick prompts:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions[activeTab]?.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setPrompt(s)}
                        className="text-xs px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-400 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Tabs>
        </div>

        {/* Gallery Panel - Hidden when 3D Model tab is active */}
        {activeTab !== '3dmodel' && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-white">Your Designs</h2>
                <p className="text-sm text-zinc-400">{images.length} visualizations</p>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1 p-4">
            {images.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4">
                  <Building2 className="h-10 w-10 text-blue-400" />
                </div>
                <p className="text-zinc-400 text-lg">No visualizations yet</p>
                <p className="text-sm text-zinc-500 mt-1">Create your first architectural render</p>
              </div>
            ) : (
              <div className={cn(
                "gap-3 sm:gap-4",
                viewMode === 'grid' ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid grid-cols-1 lg:grid-cols-2"
              )}>
                {images.map((image) => (
                  <div
                    key={image.id}
                    className="group relative aspect-video rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all"
                  >
                    <img
                      src={image.imageUrl}
                      alt={image.prompt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23666" x="50%" y="50%" text-anchor="middle" dy=".3em">No Image</text></svg>';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-xs text-white line-clamp-2 mb-2">{image.prompt}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="secondary" className="h-7 text-xs">
                                <ZoomIn className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-2">
                              <img src={image.imageUrl} alt={image.prompt} className="w-full rounded-lg" />
                              <p className="text-sm text-zinc-300 p-4">{image.prompt}</p>
                            </DialogContent>
                          </Dialog>
                          
                          {/* Use This - in current tab */}
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-7 text-xs bg-cyan-600/80 hover:bg-cyan-600 text-white"
                            onClick={() => transferImageToTab(image.imageUrl, activeTab)}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Use This
                          </Button>
                          
                          {/* Create Scene Video Button */}
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-7 text-xs bg-purple-600/80 hover:bg-purple-600 text-white"
                            onClick={() => handleCreateSceneVideo(image.imageUrl, image.prompt)}
                          >
                            <Video className="h-3 w-3 mr-1" />
                            Video
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="secondary"
                            className="h-7 text-xs"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = image.imageUrl;
                              link.download = `jaf-design-${image.id}.png`;
                              link.click();
                            }}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
        )}
      </div>
    </div>
  );
};

export default ImageGenerator;
